import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, ChevronUp, ChevronDown, ChevronsUpDown, SearchX } from 'lucide-react';
import { cn } from '@/utils/cn';

// ─── Column Definition ────────────────────────────────────────────────────────
export interface Column<T = any> {
  key: string;
  title?: string;
  header?: string | React.ReactNode;
  render?: (value: any, row: T, index: number) => React.ReactNode;
  className?: string;
  headerClassName?: string;
  minWidth?: string | number;
  sortable?: boolean;
}

// ─── Props ────────────────────────────────────────────────────────────────────
export interface TableProps<T = any> {
  columns: Column<T>[];
  data: T[];
  className?: string;
  tableClassName?: string;
  responsive?: boolean;
  emptyText?: string;
  loading?: boolean;
  skeletonRows?: number;

  // ── Pagination ──────────────────────────────────────────────────────────────
  pagination?: {
    pageSize?: number;
    showPagination?: boolean;
  };

  // ── Sorting ──────────────────────────────────────────────────────────
  isServerSideSort?: boolean;
  onSort?: (columnKey: string) => void;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  maxHeight?: string | number;
  footer?: React.ReactNode;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function getValue<T>(row: T, key: string): any {
  if (key.includes('.')) {
    return key.split('.').reduce((acc: any, part) => acc && acc[part], row as any);
  }
  return (row as any)[key];
}

// ─── Component ────────────────────────────────────────────────────────────────
function Table<T = any>({
  columns,
  data,
  className,
  tableClassName,
  responsive = true,
  emptyText = 'No data available',
  loading = false,
  skeletonRows = 5,
  pagination = { pageSize: 10, showPagination: true },
  isServerSideSort = false,
  onSort,
  sortBy,
  sortOrder: externalSortOrder,
  maxHeight,
  footer,
}: TableProps<T>) {

  // ── Pagination state ──────────────────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = pagination.pageSize || 10;
  const showPagination = pagination.showPagination !== false;

  // ── Client-side sorting state ─────────────────────────────────────────────
  const [clientSortKey, setClientSortKey] = useState<string | undefined>();
  const [clientSortOrder, setClientSortOrder] = useState<'asc' | 'desc'>('asc');

  const currentSortKey = isServerSideSort ? sortBy : clientSortKey;
  const currentSortOrder = isServerSideSort ? externalSortOrder : clientSortOrder;

  const handleSort = (col: Column<T>) => {
    if (!col.sortable) return;
    if (isServerSideSort) {
      onSort?.(col.key);
    } else {
      if (clientSortKey === col.key) {
        setClientSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
      } else {
        setClientSortKey(col.key);
        setClientSortOrder('asc');
      }
    }
  };

  // ── Sorting ───────────────────────────────────────────────────────────────
  const sortedData = useMemo(() => {
    if (isServerSideSort || !clientSortKey) return data;
    const col = columns.find(c => c.key === clientSortKey);
    if (!col?.sortable) return data;

    return [...data].sort((a, b) => {
      const aVal = getValue(a, clientSortKey);
      const bVal = getValue(b, clientSortKey);
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      const cmp = typeof aVal === 'number' && typeof bVal === 'number'
        ? aVal - bVal
        : String(aVal).localeCompare(String(bVal));
      return clientSortOrder === 'asc' ? cmp : -cmp;
    });
  }, [data, clientSortKey, clientSortOrder, columns, isServerSideSort]);

  // ── Pagination ────────────────────────────────────────────────────────────
  const totalPages = Math.ceil(sortedData.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentData = showPagination ? sortedData.slice(startIndex, endIndex) : sortedData;

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  // ── Sort icon helper ──────────────────────────────────────────────────────
  const SortIcon = ({ col }: { col: Column<T> }) => {
    if (!col.sortable) return null;
    if (currentSortKey === col.key) {
      return currentSortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
    }
    return <ChevronsUpDown size={14} />;
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className={cn(
      'bg-white dark:bg-gray-800 rounded-sm border border-gray-200 dark:border-gray-700 overflow-hidden',
      className
    )}>
      <div
        className={cn(responsive && 'overflow-x-auto', maxHeight && 'overflow-y-auto custom-scrollbar')}
        style={maxHeight ? { maxHeight } : undefined}
      >
        <table className={cn('w-full border-collapse', tableClassName)}>

          {/* ── Head ──────────────────────────────────────────────────────── */}
          <thead className="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
            <tr>
              {columns.map(col => (
                <th
                  key={col.key}
                  onClick={() => handleSort(col)}
                  style={{ minWidth: col.minWidth }}
                  className={cn(
                    'px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider transition-colors whitespace-nowrap',
                    maxHeight && 'sticky top-0 z-10 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700',
                    col.sortable && 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800',
                    col.headerClassName
                  )}
                >
                  <div className={cn(
                    "flex items-center gap-1.5 w-full",
                    col.headerClassName?.includes('text-center') && "justify-center",
                    col.headerClassName?.includes('text-right') && "justify-end"
                  )}>
                    {col.title ?? col.header}
                    {col.sortable && (
                      <span className="text-gray-400">
                        <SortIcon col={col} />
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          {/* ── Body ──────────────────────────────────────────────────────── */}
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">

            {/* Loading skeleton */}
            {loading ? (
              Array.from({ length: skeletonRows }).map((_, rowIdx) => (
                <tr key={`skeleton-${rowIdx}`} className="animate-pulse">
                  {columns.map((_, colIdx) => (
                    <td key={colIdx} className="px-6 py-4">
                      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full" />
                    </td>
                  ))}
                </tr>
              ))

              /* Empty state */
            ) : currentData.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-6 py-16 text-center">
                  <div className="flex flex-col items-center justify-center gap-3">
                    <SearchX size={48} className="text-gray-300 dark:text-gray-400" />
                    <span className="text-gray-500 dark:text-gray-300 font-medium text-sm">{emptyText}</span>
                  </div>
                </td>
              </tr>

              /* Data rows */
            ) : (
              currentData.map((row, rowIndex) => (
                <tr
                  key={rowIndex}
                  className="hover:bg-gray-50 dark:hover:bg-gray-900/30 transition-colors"
                >
                  {columns.map(col => (
                    <td
                      key={col.key}
                      className={cn('px-6 py-4 text-sm text-gray-700 dark:text-gray-300', col.className)}
                    >
                      {col.render
                        ? col.render(getValue(row, col.key), row, startIndex + rowIndex)
                        : React.isValidElement(getValue(row, col.key))
                          ? getValue(row, col.key)
                          : String(getValue(row, col.key) ?? '')}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
        {footer && (
          <div className="border-t border-gray-100 dark:border-gray-800">
            {footer}
          </div>
        )}
      </div>

      {/* ── Pagination ─────────────────────────────────────────────────────── */}
      {!loading && showPagination && totalPages > 1 && (
        <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 dark:border-gray-700">
          <div className="text-sm text-gray-500 dark:text-gray-300">
            Showing {startIndex + 1}–{Math.min(endIndex, sortedData.length)} of {sortedData.length} entries
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm text-gray-700 dark:text-gray-300 px-1">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Table;